//React
import React, { useCallback, useState } from "react";
import PropTypes from "prop-types";
import PanelCard from "../../../components/charts/PanelCard";
import TrendChart from "../../../components/charts/TrendChart";
import BreakdownBar from "../../../components/charts/BreakdownBar";
import PanelTable, { PAGE_SIZE } from "../../../components/charts/PanelTable";
import KpiStrip from "../../../components/charts/KpiStrip";
import MatrixChart from "../../../components/charts/MatrixChart";
import Loader from "../../../components/misc/loader";
import Modal from "../../../components/layout/modal";
import Button from "../../../components/inputs/button";
import { statusCopy } from "../../../components/charts/panelStatus";
import { labelEntry, unitOf } from "../../../components/charts/formatters";
import { usePanelDataset } from "./usePanelDataset";
//CSS
import "./index.scss";

/**
 * One panel, rendered from its manifest (ASB-4377, design §6): a card per
 * dataset that is not drill-only and not attached to another (A.6
 * `attachTo`), chosen by chart type, each loading its own rows for the range.
 * A row in a dataset with a `drill` opens the target dataset in a modal,
 * filtered by the row's values for the drill's keys; a drill inside the modal
 * pushes another level (silo → domains → samples), with the path as a
 * breadcrumb and a Back control. A `searchable` table (A.6) keeps its query
 * (search, sort, page) here and re-reads the API with it; a change of range,
 * region or drill filters re-reads from the first page.
 */

// The closed chart vocabulary (design §4 and A.6). A manifest naming anything
// else gets the card's "not supported" state rather than a guess.
const RENDERERS = {
	kpi: ({ dataset, rows, headline }) => (
		<KpiStrip
			dataset={dataset}
			rows={rows}
			headline={headline}
		/>
	),
	stacked_bar: ({ dataset, rows, from, to, dataFrom }) => (
		<TrendChart
			dataset={dataset}
			rows={rows}
			from={from}
			to={to}
			dataFrom={dataFrom}
		/>
	),
	line: ({ dataset, rows, from, to, dataFrom }) => (
		<TrendChart
			dataset={dataset}
			rows={rows}
			from={from}
			to={to}
			dataFrom={dataFrom}
		/>
	),
	bar: ({ dataset, rows }) => (
		<BreakdownBar
			dataset={dataset}
			rows={rows}
		/>
	),
	table: ({ dataset, rows, truncated, onDrill, drillLabel, hideColumns, server, busy }) => (
		<PanelTable
			dataset={dataset}
			rows={rows}
			truncated={truncated}
			onDrill={onDrill}
			drillLabel={drillLabel}
			hideColumns={hideColumns}
			server={server}
			busy={busy}
		/>
	),
	matrix: ({ dataset, rows, onDrill, drillLabel }) => (
		<MatrixChart
			dataset={dataset}
			rows={rows}
			onDrill={onDrill}
			drillLabel={drillLabel}
		/>
	),
};

/** The datasets attached to `target` (A.6 `attachTo`), in manifest order. */
export const attachedTo = (panel, target) =>
	(panel.datasets ?? []).filter((d) => d.attachTo === target.id && d.chart === "kpi");

/**
 * A `kpi` dataset rendered as a strip inside another dataset's card, above
 * its content: its own read, its title and description, and the same
 * states as a card in one line.
 */
export function AttachedStrip({ panel, dataset, from, to, region }) {
	const { status, data, error, reload } = usePanelDataset(panel.id, dataset.id, {
		from,
		to,
		region,
		version: panel.computedAt,
	});
	let body;
	if (status === "loading") {
		body = (
			<div className="as-attached-strip__state">
				<Loader size="sm" />
			</div>
		);
	} else if (status === "ready") {
		body = (
			<KpiStrip
				dataset={dataset}
				rows={data.rows}
			/>
		);
	} else {
		const copy = statusCopy(status, { title: dataset.title ?? dataset.id, error });
		body = (
			<p className={`as-attached-strip__state as-attached-strip__state--${status}`}>
				{copy.headline}
				{status === "failed" ? (
					<Button
						text="Try again"
						type="button"
						style="btn-simple"
						size="sm"
						disabled={false}
						loaded={false}
						onClick={reload}
					/>
				) : null}
			</p>
		);
	}
	return (
		<section
			className="as-attached-strip"
			data-dataset={dataset.id}
			data-status={status}
			aria-label={dataset.title ?? dataset.id}
		>
			<div className="as-attached-strip__heading">
				<h3 className="as-attached-strip__title">{dataset.title ?? dataset.id}</h3>
				{dataset.description ? (
					<p className="as-attached-strip__description">{dataset.description}</p>
				) : null}
			</div>
			{body}
		</section>
	);
}

AttachedStrip.propTypes = {
	panel: PropTypes.shape({ id: PropTypes.string.isRequired, computedAt: PropTypes.string }).isRequired,
	dataset: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		description: PropTypes.string,
	}).isRequired,
	from: PropTypes.string.isRequired,
	to: PropTypes.string.isRequired,
	region: PropTypes.string,
};

/** The initial server query of a searchable table: the manifest's sort, the first page. */
const initialQuery = (dataset) => ({
	q: "",
	sort: dataset.sort?.column ?? null,
	dir: dataset.sort?.dir ?? "desc",
	offset: 0,
});

export function DatasetCard({ panel, dataset, from, to, region, filters, onDrill }) {
	const searchable = Boolean(dataset.searchable) && dataset.chart === "table";
	// The range, region, drill filters and run (`computedAt`: a rerun or backfill
	// can shrink the list) a page belongs to. A change of any of
	// them reads the first page: the page was a position in a list the change
	// replaced (page 3 of a week can be past the end of a day), while the search
	// text and sort say what the admin is looking for and still apply. The page
	// is forgotten in the render that first sees the new scope (state adjusted
	// during render, React's rule for state that follows a prop; that render is
	// discarded and re-run before any effect), so the read that follows the
	// change asks for offset 0 straight away instead of the stale page first,
	// and a return to the earlier scope finds no page to restore.
	const scope = JSON.stringify({ from, to, region, filters: filters ?? {}, run: panel.computedAt ?? null });
	const [query, setQuery] = useState(() => ({ ...initialQuery(dataset), scope }));
	if (query.scope !== scope) setQuery({ ...query, offset: 0, scope });
	const onQuery = useCallback((partial) => setQuery((q) => ({ ...q, ...partial })), []);
	const offset = query.scope === scope ? query.offset : 0;
	const { status, data, error, reload, stale } = usePanelDataset(panel.id, dataset.id, {
		from,
		to,
		region,
		filters,
		version: panel.computedAt,
		...(searchable
			? {
					q: query.q,
					sort: query.sort ?? undefined,
					dir: query.sort ? query.dir : undefined,
					limit: PAGE_SIZE,
					offset,
					keepData: true,
			  }
			: {}),
	});
	const render = RENDERERS[dataset.chart];
	// A search that matches nothing is a table with no rows, not an empty day;
	// and a re-read with rows in hand keeps the table on screen, dimmed.
	let cardStatus = status;
	if (status === "ready" && !render) cardStatus = "unsupported";
	else if (status === "empty" && searchable && query.q) cardStatus = "ready";
	else if (status === "loading" && stale && data) cardStatus = "ready";
	const target = dataset.drill ? panel.datasets.find((d) => d.id === dataset.drill.to) : null;
	const drill = dataset.drill && target && onDrill ? (row) => onDrill(dataset, row) : undefined;
	const truncated = Boolean(data?.truncated);
	const headline = panel.headline?.dataset === dataset.id ? panel.headline.column : undefined;
	const strips = attachedTo(panel, dataset);
	return (
		<PanelCard
			id={dataset.id}
			title={dataset.title ?? dataset.id}
			description={dataset.description}
			computedAt={data?.computedAt ?? panel.computedAt ?? undefined}
			status={cardStatus}
			error={error ?? undefined}
			onRetry={reload}
			message={
				!render
					? `Chart type "${dataset.chart}" is not supported by this version of AMS.`
					: undefined
			}
			className={`as-dataset-card as-dataset-card--${dataset.chart}`}
			footer={
				truncated && dataset.chart !== "table"
					? "The API returned its first 1,000 rows only. Narrow the date range to see everything."
					: undefined
			}
			// The strips have their own reads and states, so they show whatever
			// state this card is in: a host that failed says so below a working strip.
			lead={
				strips.length
					? strips.map((strip) => (
							<AttachedStrip
								key={strip.id}
								panel={panel}
								dataset={strip}
								from={from}
								to={to}
								region={region}
							/>
					  ))
					: undefined
			}
		>
			{cardStatus === "ready" && render
				? render({
						dataset,
						rows: data.rows,
						// The range a time series spans, so a day with no rows is a gap.
						from,
						to,
						dataFrom: panel.dataFrom ?? undefined,
						truncated,
						onDrill: drill,
						drillLabel: target?.title,
						// The keys this card was filtered by (a drill) repeat the modal's
						// title on every row, so they are not shown.
						hideColumns: Object.keys(filters ?? {}),
						headline,
						server: searchable
							? {
									q: query.q,
									sort: query.sort,
									dir: query.dir,
									offset,
									total: data.total ?? data.rows.length,
									onChange: onQuery,
							  }
							: undefined,
						busy: status === "loading",
				  })
				: null}
		</PanelCard>
	);
}

DatasetCard.propTypes = {
	panel: PropTypes.shape({
		id: PropTypes.string.isRequired,
		computedAt: PropTypes.string,
		dataFrom: PropTypes.string,
		datasets: PropTypes.array.isRequired,
		headline: PropTypes.shape({ dataset: PropTypes.string, column: PropTypes.string }),
	}).isRequired,
	dataset: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		description: PropTypes.string,
		chart: PropTypes.string.isRequired,
		drill: PropTypes.shape({ to: PropTypes.string, on: PropTypes.arrayOf(PropTypes.string) }),
		searchable: PropTypes.bool,
		sort: PropTypes.shape({ column: PropTypes.string, dir: PropTypes.string }),
	}).isRequired,
	from: PropTypes.string.isRequired,
	to: PropTypes.string.isRequired,
	region: PropTypes.string,
	/** drill filters: `{ key: value }` from the row that was opened */
	filters: PropTypes.object,
	/** (dataset, row) => void */
	onDrill: PropTypes.func,
};

/**
 * The keys a dataset T may be filtered on, A.7's K(T): for every dataset S
 * that drills into T, S's `drill.on` keys plus the keys S itself accepts that
 * T has a column for, iterated to a fixpoint (the sets only grow and are
 * bounded by the manifest's keys, so a drill cycle ends). This is the API's
 * rule (arcspan_sandbox `admin_panels.py`, `filter_keys` /
 * `allowed_filter_keys` / `parse_filters`): any other key is a 400, so a
 * filter the UI carries into a level must pass it here first. With the
 * block_reason manifest, K(sites) = {silo} and K(samples) = {site, kind, silo},
 * so silo → domains → samples narrows to the one silo.
 */
export function allowedFilterKeys(panel, targetId) {
	const datasets = (panel.datasets ?? []).filter((d) => d && typeof d === "object");
	const columns = new Map(
		datasets.map((d) => [d.id, new Set((d.columns ?? []).map((c) => c?.key).filter(Boolean))])
	);
	const edges = datasets
		.filter((d) => d.drill && typeof d.drill === "object" && d.drill.to != null)
		.map((d) => ({ source: d.id, target: d.drill.to, on: d.drill.on ?? [] }));
	const keys = new Map();
	const keysOf = (id) => {
		if (!keys.has(id)) keys.set(id, new Set());
		return keys.get(id);
	};
	let changed = true;
	while (changed) {
		changed = false;
		for (const { source, target, on } of edges) {
			const accepted = keysOf(target);
			const targetColumns = columns.get(target) ?? new Set();
			const carried = [...keysOf(source)].filter((k) => targetColumns.has(k));
			for (const key of [...on, ...carried]) {
				if (!accepted.has(key)) {
					accepted.add(key);
					changed = true;
				}
			}
		}
	}
	return keysOf(targetId);
}

/**
 * What a drill opens its target with: `filters`, the row's values for the
 * drill's keys plus the filters of the level the row came from, for every key
 * the target accepts (allowedFilterKeys; the drill's own keys win), so a drill
 * inside a drill stays inside it; and `unfiltered`, the parent's filters the
 * target has a column for but does not accept, which the level's title says
 * it covers every value of. A card's drill has no parent, so nothing is
 * carried. Under A.7 a key the parent was filtered by and the target has a
 * column for is always accepted, so `unfiltered` is empty for a manifest the
 * API agrees with; it stays as the honest wording should the two disagree.
 */
export function drillFilters(panel, fromDataset, target, row, parentFilters) {
	const accepted = allowedFilterKeys(panel, target.id);
	const own = new Set(fromDataset.drill?.on ?? []);
	const columns = new Set((target.columns ?? []).map((c) => c.key));
	const filters = {};
	const unfiltered = {};
	Object.entries(parentFilters ?? {}).forEach(([key, value]) => {
		if (own.has(key)) return;
		if (accepted.has(key)) filters[key] = value;
		else if (columns.has(key)) unfiltered[key] = value;
	});
	own.forEach((key) => {
		filters[key] = row[key];
	});
	return { filters, unfiltered };
}

const columnOf = (dataset, key) => dataset?.columns?.find((c) => c.key === key);
const labelOf = (dataset, key) => columnOf(dataset, key)?.label ?? key;
/**
 * A filter's value as the admin reads it: the `labels` entry (A.6) for it on
 * the key's column in the dataset the row came from, or failing that in the
 * level's own dataset — "LLM only", not the stored `llm_only` — and the value
 * itself when neither maps it (a domain, a silo).
 */
const valueText = (level, key, value) =>
	(
		labelEntry(columnOf(level.parent, key), value) ??
		labelEntry(columnOf(level.dataset, key), value)
	)?.label ?? value;
const filterText = (level) =>
	Object.entries(level.filters)
		.map(([key, value]) => `${labelOf(level.parent, key)}: ${valueText(level, key, value)}`)
		.join(", ");
/** "Domain: example.com · every silo": a level's filters, and what it was not narrowed by. */
const levelText = (level) => {
	const every = Object.keys(level.unfiltered ?? {}).map(
		(key) => `every ${unitOf(labelOf(level.parent, key))}`
	);
	return [filterText(level), ...every].filter(Boolean).join(" · ");
};

export function DrillModal({ panel, stack, from, to, region, onBack, onJump, onClose, onDrill }) {
	if (stack.length === 0) return null;
	const top = stack[stack.length - 1];
	const title = levelText(top) || top.dataset.title || top.dataset.id;
	return (
		<Modal
			open
			title={title}
			onClose={onClose}
			width={1400}
			className="as-drill-modal"
		>
			{stack.length > 1 ? (
				<div className="as-drill-modal__path">
					<Button
						text="‹ Back"
						type="button"
						style="btn-simple"
						size="sm"
						disabled={false}
						loaded={false}
						onClick={onBack}
					/>
					<nav aria-label="Drill-down path">
						<ol className="as-drill-crumbs">
							{stack.map((level, i) =>
								i < stack.length - 1 ? (
									<li key={`${level.dataset.id}-${i}`}>
										<Button
											text={`${level.dataset.title ?? level.dataset.id} · ${levelText(level)}`}
											type="button"
											style="btn-simple"
											size="sm"
											disabled={false}
											loaded={false}
											onClick={() => onJump(i)}
										/>
									</li>
								) : (
									<li
										key={`${level.dataset.id}-${i}`}
										aria-current="page"
									>
										{level.dataset.title ?? level.dataset.id} · {levelText(level)}
									</li>
								)
							)}
						</ol>
					</nav>
				</div>
			) : null}
			{/* A drill from inside the modal opens its target within this level. */}
			<DatasetCard
				key={`${top.dataset.id}:${JSON.stringify(top.filters)}`}
				panel={panel}
				dataset={top.dataset}
				from={from}
				to={to}
				region={region}
				filters={top.filters}
				onDrill={(dataset, row) => onDrill(dataset, row, top.filters)}
			/>
		</Modal>
	);
}

DrillModal.propTypes = {
	panel: PropTypes.object.isRequired,
	stack: PropTypes.arrayOf(
		PropTypes.shape({
			dataset: PropTypes.object.isRequired,
			filters: PropTypes.object.isRequired,
			unfiltered: PropTypes.object,
			parent: PropTypes.object,
		})
	).isRequired,
	from: PropTypes.string.isRequired,
	to: PropTypes.string.isRequired,
	region: PropTypes.string,
	onBack: PropTypes.func.isRequired,
	onJump: PropTypes.func.isRequired,
	onClose: PropTypes.func.isRequired,
	/** (dataset, row, parentFilters) => void */
	onDrill: PropTypes.func.isRequired,
};

/** The datasets that get a card: not drill-only, not attached to another card. */
export const cardDatasets = (panel) =>
	(panel.datasets ?? []).filter((d) => !d.drillOnly && !d.attachTo);

function PanelView({ panel, from, to, region }) {
	// Each level: the dataset shown, the filters it was opened with (and the
	// parent's it could not be, see drillFilters), and the dataset the row
	// came from (for the filter labels). A card's drill has no parent filters;
	// the modal passes its top level's.
	const [stack, setStack] = useState([]);
	const open = (fromDataset, row, parentFilters) => {
		const target = panel.datasets.find((d) => d.id === fromDataset.drill?.to);
		if (!target) return;
		const { filters, unfiltered } = drillFilters(panel, fromDataset, target, row, parentFilters);
		setStack((s) => [...s, { dataset: target, filters, unfiltered, parent: fromDataset }]);
	};
	const cards = cardDatasets(panel);
	return (
		<>
			<div className="as-dashboard-cards">
				{cards.map((dataset) => (
					<DatasetCard
						key={dataset.id}
						panel={panel}
						dataset={dataset}
						from={from}
						to={to}
						region={region}
						onDrill={(dataset, row) => open(dataset, row, {})}
					/>
				))}
			</div>
			<DrillModal
				panel={panel}
				stack={stack}
				from={from}
				to={to}
				region={region}
				onBack={() => setStack((s) => s.slice(0, -1))}
				onJump={(i) => setStack((s) => s.slice(0, i + 1))}
				onClose={() => setStack([])}
				onDrill={open}
			/>
		</>
	);
}

PanelView.propTypes = {
	/** a manifest from GET /admin/panels (design A.2 + latestDay / computedAt) */
	panel: PropTypes.shape({
		id: PropTypes.string.isRequired,
		title: PropTypes.string,
		computedAt: PropTypes.string,
		datasets: PropTypes.array.isRequired,
	}).isRequired,
	from: PropTypes.string.isRequired,
	to: PropTypes.string.isRequired,
	region: PropTypes.string,
};

export default PanelView;
